/*
 * VoxPontBetaRouteGate — route-level beta gate for VoxPont variants that do not embed the
 * useVoxPontBetaAccess check themselves (voxpont2/3/20/30/40/50/60/70).
 *
 * WHAT: Blocks signed-in non-tester accounts from reaching a VoxPont variant unless they have
 *       active beta access. Dev accounts and platform admins bypass. Signed-out / anonymous users
 *       are deferred to the wrapped page, which performs its own sign-in redirect.
 * WHY: Beta access should gate ALL public variants — not only the three (4/5/100) that embed the
 *      check — so a non-tester cannot simply type e.g. /voxpont30 and use the app.
 * INVARIANT: Mirrors the gate semantics in VoxPont4Page / VoxPont100Page (active beta OR dev/admin).
 * Coupling: useVoxPontBetaAccess, VoxPontBetaGateNotice, AppRoutes.
 */
import React from "react";
import PropTypes from "prop-types";
import { useAuth } from "hooks/useAuth";
import { isDevAuthFirestoreUser } from "utils/isDevAuthFirestoreUser";
import Loading from "components/Loading";
import { useVoxPontBetaAccess } from "../useVoxPontBetaAccess";
import VoxPontBetaGateNotice from "./VoxPontBetaGateNotice";

export default function VoxPontBetaRouteGate({ children }) {
  const { user, userLoading, userData, isGlobalAdmin } = useAuth();
  const { status: betaStatus } = useVoxPontBetaAccess();

  const isRealAccount = Boolean(user) && !user?.isAnonymous;
  const bypassBetaGate = isDevAuthFirestoreUser(userData) || isGlobalAdmin;

  // Auth still resolving — show the shared full-page loader rather than flashing the page.
  if (userLoading) return <Loading page />;

  // Signed-out / anonymous: defer to the wrapped page, which redirects to its own sign-in flow
  // (every variant page already does `if (!isRealAccount) navigate(marketingHubPath())`).
  if (!isRealAccount) return children;

  // Dev accounts and platform admins always pass (parity with VoxPont4Page / VoxPont100Page).
  if (bypassBetaGate) return children;

  if (betaStatus === "loading") return <Loading page />;
  if (betaStatus === "expired") return <VoxPontBetaGateNotice variant="expired" />;
  if (betaStatus === "none") return <VoxPontBetaGateNotice variant="none" />;

  // betaStatus === 'active'
  return children;
}

VoxPontBetaRouteGate.propTypes = {
  children: PropTypes.node,
};
